import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from 'reducers';
import { Globals, Constants } from 'utils';
import { ChatService, HomeService } from 'services';
import moment from 'moment';

const MODAL = {
    NONE: 0,
    VIDEO_ROOM: 1,
};

export const useTeleconsultations = () => {
    const INITIAL_FILTERS = {
        search: '',
        since: '',
        until: '',
    };

    const nav = useHistory();
    const user = useSelector((state: RootState) => state.user);

    const [modal, setModal] = useState(MODAL.NONE);
    const [filters, setFilters] = useState(INITIAL_FILTERS);

    const [solicitudes, setSolicitudes] = useState<any[]>([]);
    const [historial, setHistorial] = useState<any[]>([]);

    const [videoRoomToken, setVideoRoomToken] = useState<string>();
    const [selectedItem, setSelectedItem] = useState({
        id: 0,
        video_room_url: '',
        client_id: '',
        affiliate_id: 0,
    });

    const getData = async () => {
        try {
            const _filters = await Globals.getUpdatedState(setFilters);
            const payload = {
                ..._filters,
                affiliate_id: user?.id,
            };
            const response: any = await HomeService.affiliate.getTeleconsultations(payload);

            if (response?.consultations?.rows) {
                const sortedData = response.consultations.rows.sort((a: any, b: any) =>
                    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                );

                const activeReqs = sortedData.filter((i: any) => i.status === 0 || i.status === 1);

                const historyReqs = sortedData.filter((i: any) => i.status === 2 || i.status === 3);

                setSolicitudes(activeReqs);
                setHistorial(historyReqs);
            }
        } catch (error) {
            console.log(error);
        }
    }
    const getVideoRoomToken = async () => {
        try {
            const accessRes: any = await HomeService.affiliate.getVideoRoomAccess();
            if (accessRes?.token) {
                const decoded = atob(accessRes.token);
                setVideoRoomToken(decoded);
                return decoded;
            }
            return null;
        } catch (error) {
            console.log(error);
        }
    }

    const onChangeFilters = <T extends keyof typeof filters>(value: any, target: T) => {
        setFilters(s => ({ ...s, [target]: value }));
    }

    const onResetFilters = () => {
        setFilters(INITIAL_FILTERS);
        getData();
    }

    const onAccept = async (item: any) => {
        Globals.confirm('¿Estás seguro de que deseas aceptar esta solicitud?', async () => {
            try {
                await HomeService.affiliate.setTeleconsultationStatus({ id: item.id, status: 1 });
                Globals.showSuccess('Consulta aceptada con éxito.');
                getData();
            } catch (error) {
                console.error('Error al aceptar:', error);
            }
        }
        );
    }

    const onReject = (item: any) => {
        Globals.confirm('¿Estás seguro de que deseas rechazar esta solicitud?', async () => {
            try {
                await HomeService.affiliate.setTeleconsultationStatus({ id: item.id, status: 2 });
                Globals.showSuccess('La solicitud ha sido rechazada.');
                getData();
            } catch (error) {
                console.error('Error al rechazar:', error);
            }
        }
        );
    }

    const onFinish = (item: any) => {
        Globals.confirm('¿Deseas dar por culminada esta teleconsulta?', async () => {
            try {
                await HomeService.affiliate.setTeleconsultationStatus({ id: item.id, status: 3 });
                Globals.showSuccess('Consulta culminada con éxito.');
                getData();
            } catch (error) {
                console.error('Error al culminar:', error);
            }
        }
        );
    }

    const onCall = async (item: any) => {
        try {
            const phoneCode = item.client?.phonecode?.name || '';
            const phoneNumber = item.client?.phone || '';
            const fullPhone = `${phoneCode}${phoneNumber}`;
            console.log("Número a llamar:", fullPhone);
            if (fullPhone) {
                await window.open(`tel:${fullPhone}`, '_blank');
            } else {
                Globals.showError('No se encontró un número de teléfono para este cliente.');
            }
        } catch (error) {
            console.log('Error en onCall:', error);
        }
    }

    const onChat = async (item: any) => {
        try {
            const chat: any = await ChatService.admin.create({ _user_id: item.client_id, doctor_id: user?.id });
            let env = '';
            if (user.affiliate_type === Constants.AFFILIATE_TYPES.MEDIC) env = 'doctor';
            else if (user.affiliate_type === Constants.AFFILIATE_TYPES.CLINIC) env = 'clinic';
            else env = 'business';

            nav.push(`/${env}/chat?chat_id=${btoa(`${chat?.id}&${moment().format('YYYYMMDD')}`)}`);
        } catch (error) {
            console.log("Error en onChat:", error);
        }
    }

    const onVideoCall = async (item: any) => {
        try {
            if (item.video_room_url) {
                if (!videoRoomToken) await getVideoRoomToken();
                setSelectedItem({
                    id: item.id,
                    video_room_url: item.video_room_url,
                    client_id: item.client_id,
                    affiliate_id: item.affiliate_id,
                });
                setModal(MODAL.VIDEO_ROOM);
            } else {
                Globals.showError('No se encontró la sala de la videollamada.');
            }
        } catch (error) {
            console.log('Error en onVideoCall:', error);
            setModal(MODAL.NONE);
        }
    }
    useEffect(() => {
        if (user?.id) {
            getData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id]);
    const canResetFilters = useMemo(() => {
        return JSON.stringify(filters) !== JSON.stringify(INITIAL_FILTERS);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    return {
        canResetFilters,
        solicitudes,
        historial,
        filters,
        modal,
        onAccept,
        onCall,
        onChangeFilters,
        onChat,
        onFinish,
        onReject,
        onResetFilters,
        onVideoCall,
        selectedItem,
        setModal,
        videoRoomToken,
        getData,
    }
}